// Lectures recalculées sur l'appareil, avec les règles du serveur.
import { ajusterEntetes, ajusterListe, enregistrerResolveur, enregistrerResolveurMotif, filtrerListe } from './registre';
import { filtrerTrajetsLocalement } from '../../filtresTrajets';
import { correspondAuxParametres } from '../readModel';
import { preparerRepartition, trouverCamionsDisponibles } from './repartition';
import { calculerProgressionCommande } from './progressionCommande';
import { affectationsDeLaCommande } from './effetsLocaux';
import { construireResumeStation, construireResumeStock, lignesDeLaStation } from './grandLivre';
import { lireGestionDepot } from './effetsLocaux';
import { calculerStatsAccueil } from './tableauDeBord';
import { calculerApercuEntretien } from './entretien';
import { formeDe, lireEntite as lireEntiteSeule, lireEntites, lectureParChemin } from '../store';
import { construireCockpit } from './cockpit';
import { calculerIndicateursEntretien } from './entretienKpis';
import { detailDuDossier, listerDossiers } from './dossiersManquants';
import { dernierReleveParCamion, evaluerPapier, evaluerPlan } from './entretien';

enregistrerResolveur('/stock/summary', async ({ parametres }) => {
  if (!(await formeDe('/stock'))) return undefined;
  let mouvements = await lireEntites('/stock');
  const { from_date: depuis, to_date: jusqua, station_id: station } = parametres || {};
  if (depuis) mouvements = mouvements.filter((m) => String(m.movement_date || '') >= depuis);
  if (jusqua) mouvements = mouvements.filter((m) => String(m.movement_date || '') <= jusqua);
  // Une station : ses cuves seulement (comme api/v1/stock.py, lot 031).
  if (station) return construireResumeStation(lignesDeLaStation(mouvements, station), station);
  const [trajets, commandes, tous] = await Promise.all([
    lireEntites('/trips'),
    lireEntites('/orders'),
    lireEntites('/stock'),
  ]);
  return construireResumeStock(mouvements, {
    managesDepot: await lireGestionDepot(tous),
    trajets,
    commandes,
  });
});

// Liste des trajets: les filtres de l'écran (« À la douane », parking, dates,
// recherche par plaque…) avec les règles du serveur. Camions et chauffeurs ne
// sont lus que pour la recherche.
filtrerListe('/trips', async (trajets, parametres) => {
  const [camions, chauffeurs] = parametres.q
    ? await Promise.all([lireEntites('/trucks'), lireEntites('/drivers')])
    : [[], []];
  return filtrerTrajetsLocalement(trajets, parametres, { camions, chauffeurs });
});

// Liste des convois: les états de l'écran (`status_group`) avec les règles de
// `api/v1/operations.py`; les autres paramètres comparés champ par champ.
filtrerListe('/operations', async (convois, { status_group: groupe, ...reste }) => {
  const retenus = convois.filter((convoi) => correspondAuxParametres(convoi, reste));
  const etat = String(groupe || 'all').trim().toLowerCase();
  if (etat === 'active' || etat === 'inactive') {
    const enCours = (convoi) => convoi.active !== false && !convoi.closed_at;
    return retenus.filter((convoi) => enCours(convoi) === (etat === 'active'));
  }
  if (etat === 'no_truck') {
    const avecCamion = new Set(
      (await lireEntites('/trips'))
        .filter((trajet) => trajet.operation_id && trajet.truck_id && !trajet.deleted)
        .map((trajet) => trajet.operation_id),
    );
    return retenus.filter(
      (convoi) => !avecCamion.has(convoi.id) && !(convoi.scheduled_trips?.truck_ids || []).some(Boolean),
    );
  }
  return retenus;
});

// Journal des frais: les lignes saisies sur l'appareil s'ajoutent aux totaux de la page.
ajusterListe('/finance/expenses', (reponse, lignes) => {
  const locales = lignes.filter((l) => l.local === true);
  if (!locales.length || !reponse.totals) return;
  const parCategorie = Object.fromEntries((reponse.totals.by_category || []).map((c) => [c.category, { ...c }]));
  locales.forEach((l) => {
    const c = l.category || 'AUTRES';
    parCategorie[c] = parCategorie[c] || { category: c, amount: 0, count: 0 };
    parCategorie[c].amount += Number(l.amount || 0);
    parCategorie[c].count += 1;
  });
  // Total de chaque mois (feuille camion groupée par mois): les lignes locales s'y ajoutent aussi.
  // Un serveur plus ancien n'envoie pas ce détail: on n'en invente pas.
  const aDesMois = Array.isArray(reponse.totals.by_month);
  const parMois = Object.fromEntries((reponse.totals.by_month || []).map((m) => [m.month ?? '', { ...m }]));
  if (aDesMois) {
    locales.forEach((l) => {
      const mois = String(l.journal_date || l.occurred_at || '').slice(0, 7);
      parMois[mois] = parMois[mois] || { month: mois || null, amount: 0, count: 0 };
      parMois[mois].amount += Number(l.amount || 0);
      parMois[mois].count += 1;
    });
  }
  reponse.totals = {
    ...reponse.totals,
    amount: Number(reponse.totals.amount || 0) + locales.reduce((s, l) => s + Number(l.amount || 0), 0),
    count: Number(reponse.totals.count || 0) + locales.length,
    by_category: Object.values(parCategorie),
    ...(aDesMois
      ? { by_month: Object.values(parMois).sort((a, b) => String(b.month || '').localeCompare(String(a.month || ''))) }
      : {}),
  };
});

// Liste des camions: propres / loués, comme les en-têtes du serveur.
ajusterEntetes('/trucks', (entetes, camions) => {
  const loues = camions.filter((c) => c.ownership_type === 'LOUE').length;
  return { ...entetes, 'x-count-loues': String(loues), 'x-count-propres': String(camions.length - loues) };
});

// Chiffres de l'accueil.
enregistrerResolveur('/dashboard/stats', async ({ parametres }) => {
  if (!(await formeDe('/orders')) || !(await formeDe('/trucks'))) return undefined;
  const [factures, commandes, clients, camions, trajets, convois, chauffeurs, mouvements] = await Promise.all([
    lireEntites('/invoices'), lireEntites('/orders'), lireEntites('/clients'), lireEntites('/trucks'),
    lireEntites('/trips'), lireEntites('/operations'), lireEntites('/drivers'), lireEntites('/stock'),
  ]);
  return {
    success: true,
    data: calculerStatsAccueil({
      periode: parametres?.period || 'month',
      factures, commandes, clients, camions, trajets, convois, chauffeurs, mouvements,
    }),
  };
});

// Répartition: camions proposés, préparation, reste à affecter.
async function contexteRepartition(orderId) {
  const commande = await lireEntiteSeule('/orders', orderId);
  if (!commande) return null;
  const [trajets, camions, chauffeurs] = await Promise.all([
    lireEntites('/trips'), lireEntites('/trucks'), lireEntites('/drivers'),
  ]);
  const parId = Object.fromEntries(trajets.map((t) => [String(t.id), t]));
  const affectations = affectationsDeLaCommande(commande);
  const restant = calculerProgressionCommande(commande, affectations, parId).to_allocate_liters;
  const disponibles = trouverCamionsDisponibles({
    quantiteCommande: commande.quantity_liters || 0,
    destination: commande.delivery_location,
    restant,
    trajets,
    camions,
    chauffeurs,
  });
  return { commande, disponibles, affectations, parId };
}

enregistrerResolveurMotif(/^\/orders\/([^/]+)\/available-trucks$/, async ({ correspondance }) => {
  const contexte = await contexteRepartition(decodeURIComponent(correspondance[1]));
  return contexte ? { success: true, data: contexte.disponibles } : undefined;
});

enregistrerResolveurMotif(/^\/orders\/([^/]+)\/dispatch-readiness$/, async ({ correspondance }) => {
  const contexte = await contexteRepartition(decodeURIComponent(correspondance[1]));
  if (!contexte) return undefined;
  return preparerRepartition({
    commande: contexte.commande,
    camionsDisponibles: contexte.disponibles,
    autoriserPartiel: Boolean(contexte.commande.allow_partial_dispatch),
    litresCiterne: contexte.commande.cistern_liters_snapshot || null,
    affectations: contexte.affectations,
    trajets: contexte.parId,
  });
});

// Entretien: aperçu des échéances et des blocages.
enregistrerResolveur('/maintenance/overview', async () => {
  if (!(await formeDe('/maintenance/work-orders'))) return undefined;
  const [plans, papiers, ordres, pannes, immobilisations, releves] = await Promise.all([
    lireEntites('/maintenance/plans'), lireEntites('/maintenance/documents'),
    lireEntites('/maintenance/work-orders'), lireEntites('/maintenance/breakdowns'),
    lireEntites('/maintenance/immobilizations'), lireEntites('/maintenance/readings'),
  ]);
  return { success: true, data: calculerApercuEntretien({ plans, papiers, ordres, pannes, immobilisations, releves }) };
});

// Indicateurs de parc: fenêtre glissante de `period_days` jours, jusqu'à maintenant.
enregistrerResolveur('/maintenance/kpis', async ({ parametres }) => {
  if (!(await formeDe('/maintenance/work-orders'))) return undefined;
  const [plans, papiers, ordres, pannes, immobilisations, releves] = await Promise.all([
    lireEntites('/maintenance/plans'), lireEntites('/maintenance/documents'),
    lireEntites('/maintenance/work-orders'), lireEntites('/maintenance/breakdowns'),
    lireEntites('/maintenance/immobilizations'), lireEntites('/maintenance/readings'),
  ]);
  const derniers = dernierReleveParCamion(releves);
  const valeur = (camion) => {
    const n = Number.parseFloat(derniers[camion]?.value);
    return derniers[camion] && Number.isFinite(n) ? n : null;
  };
  return {
    success: true,
    data: calculerIndicateursEntretien({
      immobilisations, ordres, pannes, releves,
      plans: plans.map((p) => evaluerPlan(p, valeur(p.truck_id))),
      papiers: papiers.filter((p) => p.is_active !== false).map((p) => evaluerPapier(p)),
      joursPeriode: parametres?.period_days,
    }),
  };
});

// Dossiers de manquants.
enregistrerResolveur('/shortages', async ({ parametres }) => {
  if (!(await formeDe('/shortages'))) return undefined;
  return listerDossiers(parametres || {});
});
enregistrerResolveurMotif(/^\/shortages\/trips\/([^/]+)$/, async ({ correspondance }) =>
  detailDuDossier(decodeURIComponent(correspondance[1])),
);

// Cockpit direction: transport et carte « stock et alertes » recalculés; les sections
// stations et contrôle carburant gardent leur dernière version connue.
enregistrerResolveur('/dashboard/business-cockpit', async () => {
  const lectures = (await lectureParChemin('/dashboard/business-cockpit'))
    .filter((e) => e.collection === false)
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));
  const avecEntrees = lectures.find((e) => (e.data?.data ?? e.data)?.inputs);
  const dernier = avecEntrees || lectures[0];
  const instantane = dernier?.data?.data ?? dernier?.data;
  if (!instantane || !avecEntrees) return undefined;
  if (!(await formeDe('/orders')) || !(await formeDe('/trips'))) return undefined;
  const [commandes, trajets, factures, mouvements, alertes] = await Promise.all([
    lireEntites('/orders'), lireEntites('/trips'), lireEntites('/invoices'), lireEntites('/stock'), lireEntites('/alerts/events'),
  ]);
  const sansDepot = !(await lireGestionDepot(mouvements));
  let litresCharges = null;
  if (sansDepot) {
    const resume = construireResumeStock(mouvements, { managesDepot: false, trajets, commandes });
    litresCharges = Math.round(resume.current_stock_liters * 100) / 100;
  }
  const parCle = Object.fromEntries((instantane.sections || []).map((s) => [s.key, s]));
  return {
    success: true,
    data: {
      ...construireCockpit({
        pack: instantane.active_pack,
        modules: instantane.enabled_modules,
        commandes, trajets, factures, mouvements, alertes,
        scores: instantane.inputs.mission_integrity_scores || [],
        packs: instantane.inputs.delivery_proof_packs || [],
        litresCharges,
        stations: parCle.stations || null,
        controle: parCle.control_fuel || null,
      }),
      inputs: instantane.inputs,
    },
  };
});
